import React from 'react'
import { LocationDescriptorObject } from 'history'
import { useLocation } from 'react-router'

import {
  FrameGroupDetailsFragment,
  SymbolFragment,
  useGetComponentsQuery,
} from '@sketch/gql-types'
import { dataIdFromObject } from '@sketch/graphql-cache'
import { Dropdown, handleFetchMore, InfiniteList } from '@sketch/components'

import { useVersioning } from '../../../versioning'
import NavigationItem from './NavigationItem'

interface SymbolNavigationItemProps {
  shareIdentifier: string
  frameGroup: FrameGroupDetailsFragment
}

const ENTRIES_PATH = ['share', 'version', 'document', 'components', 'entries']

const SymbolDropdown = (props: SymbolNavigationItemProps) => {
  const { shareIdentifier, frameGroup } = props

  const { versionShortId, getPathname } = useVersioning()
  const { state } = useLocation()

  const { data, loading, fetchMore } = useGetComponentsQuery({
    variables: {
      shareIdentifier,
      path: frameGroup.component?.path,
      versionShortId,
      type: 'SYMBOL',
    },
  })

  const symbols = data?.share?.version?.document?.components.entries || []
  const after = data?.share?.version?.document?.components.meta.after

  const onLoadMore = handleFetchMore(fetchMore, ENTRIES_PATH, {
    dataIdFromObject,
    after,
  })

  const getFrameLink = (symbol: SymbolFragment): LocationDescriptorObject => {
    return {
      pathname: getPathname({
        routeKey: 'FRAME',
        routeParams: {
          shareID: shareIdentifier,
          frameUUID: symbol.frame!.uuid!,
        },
      }),
      state,
    }
  }

  if (loading) {
    return (
      <>
        <Dropdown.Header>Symbols</Dropdown.Header>
        <Dropdown.Skeleton />
      </>
    )
  }

  return (
    <InfiniteList onLoadMore={onLoadMore}>
      <Dropdown.Header>Symbols</Dropdown.Header>
      {symbols.map(symbol => (
        <Dropdown.Item.NavLink
          key={symbol.identifier}
          isActive={match =>
            !!match || symbol.identifier === frameGroup?.identifier
          }
          to={getFrameLink(symbol as SymbolFragment)}
        >
          {symbol.name}
        </Dropdown.Item.NavLink>
      ))}
    </InfiniteList>
  )
}

export const SymbolNavigationItem = (props: SymbolNavigationItemProps) => {
  const { shareIdentifier, frameGroup } = props

  const { data } = useGetComponentsQuery({
    variables: {
      shareIdentifier,
      path: frameGroup.component?.path,
      type: 'SYMBOL',
      limit: 2,
    },
  })

  if (!frameGroup || !frameGroup.component) {
    return null
  }

  const hasComponents =
    (data?.share?.version?.document?.components.meta.totalCount || 0) > 1

  return (
    <NavigationItem
      icon="symbol"
      label={frameGroup.component.name || ''}
      {...(hasComponents
        ? {
            dropdownComponent: SymbolDropdown,
            dropdownComponentProps: props,
            dropdownProps: { placement: 'bottom-start' },
          }
        : {})}
    />
  )
}
